interface Props {
  /** Nom du restaurant — la marque de repli, et le texte lu par un lecteur d'écran. */
  name: string;
  logoUrl?: string | null;
  /** Logo clair, servi seulement si le logo principal manque : le carnet est
   *  posé sur `--paper`, un fond clair, donc le logo normal est le bon. */
  logoInverseUrl?: string | null;
  /** `chip` — la marque glissée dans une rangée qui existe déjà (la rangée
   *  d'onglets du carnet) ; `plate` — la marque en clair, à la place du titre
   *  de l'écran de code. */
  variant?: 'chip' | 'plate';
}

/**
 * SC-979 — l'identité du restaurant sur la surface du personnel. Un serveur
 * qui tourne sur deux établissements installe deux carnets identiques : hors
 * le nom écrit sous l'icône, rien à l'écran ne dit lequel des deux il vient
 * d'ouvrir.
 *
 * La contrainte est de ne coûter AUCUNE rangée : le `chip` se pose dans la
 * rangée d'onglets déjà là (48px de haut, il en occupe 32) et la `plate`
 * REMPLACE le titre « Code d'accès » du pavé — elle ne s'ajoute pas au-dessus.
 *
 * Le logo garde SES proportions (`w-auto`, plafonné à 40px) au lieu d'être logé
 * dans un carré : la revue visuelle a mesuré un logo portrait rendu à 17,5px de
 * large dans un cadre de 32 — 45 % de la largeur payée en blanc, et prise sur
 * les libellés d'onglets voisins. Seul le repli sans logo est un carré,
 * puisque l'initiale, elle, en remplit un.
 *
 * Sans logo, le carré porte l'initiale sur la couleur de marque du restaurant
 * (`--mint` / `--on-primary`, posées par `applyTheme`) : deux établissements
 * se distinguent alors par la couleur, pas seulement par une lettre. Le rond
 * sombre à initiale de la même rangée est, lui, le serveur — la forme (carré
 * contre rond) est ce qui sépare les deux.
 */
export default function RestaurantMark({ name, logoUrl, logoInverseUrl, variant = 'chip' }: Props) {
  const src = logoUrl || logoInverseUrl || null;
  const label = name.trim();

  if (variant === 'plate') {
    // Sans logo, le nom en toutes lettres : un écran de verrouillage doit dire
    // DE QUI il garde la salle, et une initiale seule ne le dit pas.
    if (!src) {
      return (
        <span data-testid="restaurant-mark" className="text-center">
          {label}
        </span>
      );
    }
    // Le cadre n'est pas décoratif : la plupart des logos sont livrés sur fond
    // blanc opaque, et posé nu sur le crème du pavé un tel logo se lisait comme
    // un rectangle blanc oublié là (revue visuelle, 2026-09-13).
    return (
      <span
        data-testid="restaurant-mark"
        className="inline-flex items-center justify-center rounded-xl border border-hairline bg-paper p-1.5"
      >
        <img
          src={src}
          alt={label}
          // Le pavé se resserre déjà sous 640px de haut : la marque suit, sinon
          // c'est « Annuler le changement » qui repasse sous le pli.
          className="h-12 [@media(max-height:640px)]:h-9 w-auto max-w-[60vw] object-contain"
        />
      </span>
    );
  }

  // Pas de cadre ici, contrairement a la `plate` : le cadre existe pour qu'un
  // logo sur fond blanc ne flotte pas sur le creme du pave. La rangee, elle,
  // est deja sur `--paper` — le cadre n'y achete rien, et un liser que le
  // dessin touche a 1px se lit comme une image rognee par sa boite.
  if (src) {
    return (
      <img
        data-testid="restaurant-mark"
        src={src}
        alt={label}
        className="shrink-0 h-8 w-auto max-w-[40px] ms-1.5 me-1.5 object-contain"
      />
    );
  }

  return (
    <span
      data-testid="restaurant-mark"
      role="img"
      aria-label={label}
      title={label}
      className="shrink-0 w-8 h-8 ms-1.5 me-1.5 rounded-md flex items-center justify-center"
      style={{ backgroundColor: 'var(--mint)', color: 'var(--on-primary)' }}
    >
      <span aria-hidden="true" className="font-sans text-[13px] font-bold leading-none">
        {markInitials(label)}
      </span>
    </span>
  );
}

/** « Chez Loulou » → « CL », « 福记小馆 » → « 福 ». Deux lettres latines tiennent
 *  dans 32px à 13px/700 ; un idéogramme, non — et un seul suffit à le lire. */
export function markInitials(name: string): string {
  const clean = name.trim();
  if (!clean) return '?';
  if (/[\u3000-\u9FFF\uAC00-\uD7AF\uFF00-\uFFEF]/.test(clean[0])) return clean[0];
  const words = clean.split(/\s+/).filter((w) => /[\p{L}\p{N}]/u.test(w));
  if (words.length === 0) return '?';
  return words
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase();
}
